import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IUserPublic } from '~/types/user'

import TodoDetailsGeneralInformation from './GeneralInformation'
import OrganisationChart from './OrganisationChart'

import { checklistsKeys, useChecklistsDetailsQuery } from '../queries'

import BackNavigation from '~/components/BackNavigation'
import ChecklistModal from '~/components/ChecklistModal'
import MwMLoader from '~/components/MwMLoader'
import ViewTitle from '~/components/ViewTitle'
import { useUserDetailsQueries, useUserDetailsQuery } from '~/queries/users'
import { keys as versionsKeys, useVersionsDetailsQuery } from '~/queries/versions'
import { displayToast } from '~/utils/toast'

import './styles.scss'

type BasicUserInfo = {
  date: number
  job: string
  name: string
  picture?: string
}

const ChecklistDetails = (): ReactElement | null => {
  /** Local state */

  const { appId, checklistId } = useParams<{ appId: string; checklistId: string }>() as {
    appId: string
    checklistId: string
  }

  const queryClient = useQueryClient()

  const [isModalOpen, setIsModalOpen] = useState(false)

  /** Queries & Mutations */

  const { data: checklist, isLoading: checklistLoading } = useChecklistsDetailsQuery(checklistId)

  const { data: version, isLoading: versionLoading } = useVersionsDetailsQuery(
    { appId, versionId: checklist?.application_version_id },
    { enabled: !!checklist?.application_version_id }
  )

  const formatUser = ({ name, picture, profile }: IUserPublic) => ({
    date: checklist?.created_at_ts || 0,
    job: profile?.job_title || '',
    name,
    picture,
  })

  const { data: createdByUser } = useUserDetailsQuery(checklist?.created_by, !!checklist, formatUser)

  const { data: updatedByUser } = useUserDetailsQuery(checklist?.updated_by, !!checklist, formatUser)

  const teamMemberIds = checklist
    ? [...new Set(Object.values(checklist.assignation_mapping).reduce((prev, users) => [...prev, ...users], []))]
    : []

  const userQueries = useUserDetailsQueries(teamMemberIds, formatUser)

  const teamMembers = userQueries.some((e) => e.isLoading)
    ? []
    : userQueries.map(({ data }) => data as BasicUserInfo).filter((value) => value)

  /** Handlers */

  const handleEditTodo = (value: boolean) => () => setIsModalOpen(value)

  const handleEditSuccess = () => {
    setIsModalOpen(false)
    queryClient.invalidateQueries({ queryKey: checklistsKeys.details(checklistId) })

    if (checklist?.application_version_id) {
      queryClient.invalidateQueries({ queryKey: versionsKeys.details(checklist.application_version_id) })
    }

    displayToast('To-do successfully updated', 'success')
  }

  const handleEditFailure = () => {
    setIsModalOpen(false)
    displayToast('To-do could not be updated', 'error')
  }

  /** Render */

  if (checklistLoading || (!!checklist?.application_version_id && versionLoading)) {
    return (
      <div className="checklist-details-container loader-container">
        <MwMLoader category="general" />
      </div>
    )
  }

  if (!checklist) {
    return null
  }

  const { application_id, application_semantic_version, assignation_mapping, due_date, status, steps, title } =
    checklist

  return (
    <div className="checklist-details-container">
      <ViewTitle>
        <BackNavigation title="To-do lists" to="/checklists" />
      </ViewTitle>
      <div className="checklist-details-layout">
        <TodoDetailsGeneralInformation
          changelog={version?.changelog}
          createdBy={createdByUser}
          onEditTodo={handleEditTodo(true)}
          releaseDate={due_date}
          status={status}
          team={teamMembers}
          title={title}
          updatedBy={updatedByUser}
          version={application_semantic_version}
        />
        <OrganisationChart steps={steps} teams={assignation_mapping} />
      </div>
      {checklist ? (
        <ChecklistModal
          appId={application_id}
          checklist={checklist}
          isOpen={isModalOpen}
          onClose={handleEditTodo(false)}
          onFailure={handleEditFailure}
          onSuccess={handleEditSuccess}
        />
      ) : null}
    </div>
  )
}

export default ChecklistDetails
